/**
 * Safe JSON parsing utilities.
 *
 * Provides type-safe JSON parsing with optional Zod validation.
 * Use instead of raw JSON.parse to handle errors gracefully.
 */

import type { z } from "zod";

// =============================================================================
// TYPES
// =============================================================================

/** Result of safe JSON parse operation */
export type SafeParseResult<T> =
  | { success: true; data: T }
  | { success: false; error: Error };

// =============================================================================
// FUNCTIONS
// =============================================================================

/**
 * Safely parse JSON with optional default value.
 *
 * @param json - JSON string to parse
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed value or default
 *
 * @example
 * ```ts
 * const settings = safeJsonParse(localStorage.getItem("settings"), {});
 * ```
 */
export function safeJsonParse<T>(
  json: string | null | undefined,
  defaultValue: T
): T {
  if (!json) return defaultValue;

  try {
    return JSON.parse(json) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Safely parse JSON with detailed result.
 *
 * @param json - JSON string to parse
 * @returns Parse result with success/error status
 *
 * @example
 * ```ts
 * const result = safeJsonParseResult<Settings>(jsonString);
 * if (result.success) {
 *   console.log(result.data);
 * } else {
 *   console.error(result.error);
 * }
 * ```
 */
export function safeJsonParseResult<T>(
  json: string | null | undefined
): SafeParseResult<T> {
  if (!json) {
    return { success: false, error: new Error("Empty input") };
  }

  try {
    const data = JSON.parse(json) as T;
    return { success: true, data };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err : new Error("Parse error"),
    };
  }
}

/**
 * Safely parse JSON with Zod schema validation.
 *
 * @param json - JSON string to parse
 * @param schema - Zod schema for validation
 * @param defaultValue - Default value if parsing or validation fails
 * @returns Validated data or default
 *
 * @example
 * ```ts
 * const SettingsSchema = z.object({ theme: z.string() });
 * const settings = safeJsonParseWithSchema(
 *   localStorage.getItem("settings"),
 *   SettingsSchema,
 *   { theme: "light" }
 * );
 * ```
 */
export function safeJsonParseWithSchema<T>(
  json: string | null | undefined,
  schema: z.ZodSchema<T>,
  defaultValue: T
): T {
  if (!json) return defaultValue;

  try {
    const parsed = JSON.parse(json);
    const result = schema.safeParse(parsed);
    return result.success ? result.data : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Safely parse JSON with Zod schema and detailed result.
 *
 * @param json - JSON string to parse
 * @param schema - Zod schema for validation
 * @returns Parse result with success/error status and validated data
 *
 * @example
 * ```ts
 * const result = safeJsonParseWithSchemaResult(jsonString, SettingsSchema);
 * if (result.success) {
 *   // result.data is fully typed
 *   console.log(result.data.theme);
 * }
 * ```
 */
export function safeJsonParseWithSchemaResult<T>(
  json: string | null | undefined,
  schema: z.ZodSchema<T>
): SafeParseResult<T> {
  if (!json) {
    return { success: false, error: new Error("Empty input") };
  }

  try {
    const parsed = JSON.parse(json);
    const result = schema.safeParse(parsed);

    if (result.success) {
      return { success: true, data: result.data };
    }

    return {
      success: false,
      error: new Error(result.error.message),
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err : new Error("Parse error"),
    };
  }
}

/**
 * Safely stringify data to JSON.
 *
 * @param data - Data to stringify
 * @param defaultValue - Default value if stringify fails (e.g., circular references)
 * @returns JSON string or default
 */
export function safeJsonStringify(
  data: unknown,
  defaultValue = ""
): string {
  try {
    return JSON.stringify(data);
  } catch {
    return defaultValue;
  }
}

/**
 * Safely parse Response.json() with logging for parse errors.
 * 
 * Use this instead of `response.json().catch(() => ({}))` to avoid
 * silently swallowing errors while still handling invalid JSON gracefully.
 * 
 * @param response - Fetch Response object
 * @param context - Optional context for error logging
 * @returns Parsed JSON or empty object
 * 
 * @example
 * ```ts
 * if (!response.ok) {
 *   const data = await safeParseResponseJson(response, "upload error");
 *   throw new Error(data.error || "Upload failed");
 * }
 * ```
 */
export async function safeParseResponseJson<T = Record<string, unknown>>(
  response: Response,
  context?: string
): Promise<T> {
  try {
    return await response.json() as T;
  } catch (err) {
    // Log the parse error for debugging but don't throw
    // This handles cases where server returns non-JSON error responses
    if (import.meta.env.DEV) {
      console.warn(
        `[safeParseResponseJson] Failed to parse JSON${context ? ` (${context})` : ""}:`,
        err
      );
    }
    return {} as T;
  }
}
